/**
 * The silos over the days (16 October 2026): the litres in Gwalchmai's silos as one line through time.
 *
 * Ronald: "zonder dat tijdverloop is het toch totaal zinloos die informatie". Each batch is a step down at
 * its time, each tanker a step up at its time. What the plant's meter logged is solid; what is expected
 * (a tanker from the collection rhythm, a batch from the cheese plan) is dashed. The plant runs its batches
 * back to back from 04:00 (Ronald, 16 October 2026): a batch the silos cannot fill at its time is not made,
 * nor any after it that day, and is drawn in the critical colour where the plan had it. There are no words in
 * the drawing: the days, the hours and the litres on the axes, and a
 * title on each mark for the pointer. The explanation is in the page's Explainer; the same figures are in
 * the tables under it, which is what a screen reader reads.
 *
 * The SVG's viewBox is the container's width in pixels, so text stays the design system's size; under
 * MIN_DAY_WIDTH px a day, the drawing keeps that width and scrolls inside its own box.
 */
import { useLayoutEffect, useRef, useState } from "react";

import type { TimelineBlockedBatch, TimelinePoint } from "../api/milkFlow";
import { formatLitres } from "../time";

const HEIGHT = 300;
const PLOT_TOP = 30;
const PLOT_BOTTOM = 22;
const PLOT_LEFT = 64;
const PLOT_RIGHT = 10;
const MIN_DAY_WIDTH = 72;
const FALLBACK_WIDTH = 900;
const DAY_MINUTES = 1440;

const WEEKDAY = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric" });

/** Minutes since the window's first midnight, from the API's local wall time `YYYY-MM-DDTHH:MM`. */
function minutesFrom(first: string, at: string): number {
  const [d, t = "00:00"] = at.split("T") as [string, string?];
  const days = (Date.parse(`${d}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / 86400000;
  return days * DAY_MINUTES + Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
}

function dayLabel(first: string, i: number): string {
  return WEEKDAY.format(new Date(Date.parse(`${first}T00:00:00Z`) + i * 86400000));
}

const signed = (n: number) => (n > 0 ? `+${formatLitres(n)}` : `−${formatLitres(-n)}`);

/** What a mark is, for the pointer's title. */
function titleOf(p: TimelinePoint): string {
  const when = `${WEEKDAY.format(new Date(`${p.at.slice(0, 10)}T00:00:00Z`))} ${p.at.slice(11, 16)}`;
  const what =
    p.kind === "batch" || p.kind === "planned_batch"
      ? `Batch${p.sequence ? ` ${p.sequence}` : ""}`
      : p.kind === "arrival"
        ? `Tanker, ${p.source === "own" ? "our own" : "J.H. Willis"}${p.farms?.length ? `: ${p.farms.join(", ")}` : ""}`
        : p.kind === "unload"
          ? "Tanker"
          : p.kind;
  const short = p.short_litres ? `, ${formatLitres(p.short_litres)} short, not made` : "";
  const after = p.after === null ? "" : `, silos ${formatLitres(Math.max(p.after, 0))}`;
  return `${when} ${what} ${p.change === null || p.change === 0 ? "" : signed(p.change)}${after}${short}`;
}

function tickStep(top: number): number {
  for (const step of [5000, 10000, 20000, 25000, 50000, 100000]) if (top / step <= 6) return step;
  return 100000;
}

function useWidth() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      if (w > 0) setWidth(w);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/** The sentence the drawing is read as. */
export function timelineSummary(points: TimelinePoint[], first: string, days: number): string {
  const levels = points.filter((p) => p.after !== null);
  if (levels.length === 0) return "The silos are not known for these days.";
  const short = points.filter((p) => p.status === "short");
  const lowest = Math.min(...levels.map((p) => p.after!));
  const day = (at: string) => WEEKDAY.format(new Date(`${at.slice(0, 10)}T00:00:00Z`));
  const n = (k: number) => `${k} ${k === 1 ? "batch" : "batches"}`;
  const parts = [`Silos from ${dayLabel(first, 0)} to ${dayLabel(first, days - 1)}`, `lowest ${formatLitres(Math.max(lowest, 0))}`];
  if (short.length === 0) parts.push("every batch on its time");
  if (short.length > 0) parts.push(`${n(short.length)} not made: ${short.map((p) => `${day(p.at)} ${p.at.slice(11, 16)}`).join(", ")}`);
  return `${parts.join("; ")}.`;
}

export function SiloTimeline({
  points,
  first,
  days,
  today,
  through,
  blocked = [],
  red = [],
}: {
  points: TimelinePoint[];
  /** The days with milk too old even thermised (TH-3): a spot sale needed. Their column is tinted in the critical colour. */
  red?: string[];
  /** The batches the production calendar blocks: a grey mark at their time, with no step (no milk taken). */
  blocked?: TimelineBlockedBatch[];
  first: string;
  days: number;
  today: string;
  through: string | null;
}) {
  const [ref, measured] = useWidth();
  const width = Math.max(measured, PLOT_LEFT + PLOT_RIGHT + days * MIN_DAY_WIDTH);
  const plotW = width - PLOT_LEFT - PLOT_RIGHT;
  const plotH = HEIGHT - PLOT_TOP - PLOT_BOTTOM;
  const total = days * DAY_MINUTES;
  const all = points.filter((p) => p.after !== null);
  const highest = Math.max(10000, ...all.map((p) => p.after!));
  const step = tickStep(highest * 1.08);
  const top = Math.ceil((highest * 1.08) / step) * step;
  const x = (at: string) => PLOT_LEFT + (Math.min(Math.max(minutesFrom(first, at), 0), total) / total) * plotW;
  const y = (litres: number) => PLOT_TOP + plotH - (Math.max(litres, 0) / top) * plotH;
  const dayW = plotW / days;
  const todayIndex = (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / 86400000;

  const path = (ps: TimelinePoint[], cls: string) => {
    const known = ps.filter((p) => p.after !== null);
    const out = [];
    for (let i = 1; i < known.length; i += 1) {
      const a = known[i - 1]!;
      const b = known[i]!;
      const dashed = b.basis === "expected" ? " is-expected" : "";
      const empty = a.after! < 0 ? " is-empty" : "";
      out.push(
        <path
          className={`mf-tl__line${cls}${dashed}${empty}`}
          d={`M${x(a.at)},${y(a.after!)} H${x(b.at)}`}
          key={`h${i}`}
        />,
      );
      out.push(
        <path
          className={`mf-tl__line${cls}${dashed}${b.after! < 0 && a.after! < 0 ? " is-empty" : ""}`}
          d={`M${x(b.at)},${y(a.after!)} V${y(b.after!)}`}
          key={`v${i}`}
        />,
      );
    }
    return out;
  };

  const marks = points
    .map((p, i) => {
      const before = points.slice(0, i).reverse().find((q) => q.after !== null)?.after ?? p.after;
      if (p.after === null || before === null || before === undefined) return null;
      const px = x(p.at);
      if (p.kind === "batch" || p.kind === "planned_batch") {
        const cls =
          p.status === "short" ? " is-short" : p.basis === "expected" ? " is-expected" : "";
        return (
          <circle className={`mf-tl__batch${cls}`} cx={px} cy={y(before)} key={`b${i}`} r={p.status === "short" ? 6 : 4}>
            <title>{titleOf(p)}</title>
          </circle>
        );
      }
      if (p.kind === "unload" || p.kind === "arrival") {
        const ty = y(p.after);
        return (
          <path
            className={`mf-tl__tanker${p.basis === "expected" ? " is-expected" : ""}${p.source === "own" ? " is-own" : ""}`}
            d={`M${px - 5},${ty + 8} L${px},${ty} L${px + 5},${ty + 8} Z`}
            key={`t${i}`}
          >
            <title>{titleOf(p)}</title>
          </path>
        );
      }
      return null;
    })
    .filter(Boolean);

  // A blocked batch sits on the line at its time: the level it would have drawn from, unchanged.
  const known = points.filter((p) => p.after !== null);
  const blockedMarks = blocked.map((b) => {
    const at = Date.parse(b.at);
    const level = [...known].reverse().find((p) => Date.parse(p.at) <= at)?.after ?? null;
    // Past the line's end (a week nothing is traded for yet, US-6 A) there is no level to sit on.
    const lineEnd = known.length > 0 ? Date.parse(known[known.length - 1]!.at) : 0;
    if (level === null || at > lineEnd || minutesFrom(first, b.at) < 0 || minutesFrom(first, b.at) > total) return null;
    return (
      <rect className="mf-tl__blocked" height={8} key={`x${b.day}-${b.sequence}`} width={8} x={x(b.at) - 4} y={y(level) - 4}>
        <title>{`Batch ${b.sequence} blocked by the production calendar`}</title>
      </rect>
    );
  });

  const yTicks: number[] = [];
  for (let v = 0; v <= top; v += step) yTicks.push(v);
  const hourLabels = dayW >= 90;

  return (
    <div className="mf-tl" ref={ref}>
      <svg
        aria-label={timelineSummary(points, first, days)}
        className="mf-tl__svg"
        role="img"
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width={width}
      >
        <g aria-hidden="true">
          {todayIndex >= 0 && todayIndex < days ? (
            <rect className="mf-tl__today" height={plotH} width={dayW} x={PLOT_LEFT + todayIndex * dayW} y={PLOT_TOP} />
          ) : null}
          {red.map((d) => {
            const i = (Date.parse(`${d}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / 86400000;
            return i < 0 || i >= days ? null : (
              <rect className="mf-tl__red" height={plotH} key={`red${d}`} width={dayW} x={PLOT_LEFT + i * dayW} y={PLOT_TOP} />
            );
          })}
          {yTicks.map((v) => (
            <g key={`y${v}`}>
              <line className="mf-tl__grid" x1={PLOT_LEFT} x2={PLOT_LEFT + plotW} y1={y(v)} y2={y(v)} />
              <text className="mf-tl__tick" textAnchor="end" x={PLOT_LEFT - 6} y={y(v) + 4}>
                {formatLitres(v)}
              </text>
            </g>
          ))}
          {Array.from({ length: days }, (_, i) => (
            <g key={`d${i}`}>
              <line className="mf-tl__day" x1={PLOT_LEFT + i * dayW} x2={PLOT_LEFT + i * dayW} y1={PLOT_TOP - 22} y2={PLOT_TOP + plotH} />
              <text
                className={`mf-tl__daylabel${i === todayIndex ? " is-today" : ""}`}
                x={PLOT_LEFT + i * dayW + 6}
                y={PLOT_TOP - 10}
              >
                {dayLabel(first, i)}
              </text>
              {[6, 12, 18].map((h) => (
                <g key={h}>
                  <line
                    className="mf-tl__hour"
                    x1={PLOT_LEFT + (i + h / 24) * dayW}
                    x2={PLOT_LEFT + (i + h / 24) * dayW}
                    y1={PLOT_TOP + plotH}
                    y2={PLOT_TOP + plotH + 4}
                  />
                  {hourLabels ? (
                    <text className="mf-tl__tick" textAnchor="middle" x={PLOT_LEFT + (i + h / 24) * dayW} y={HEIGHT - 6}>
                      {String(h).padStart(2, "0")}
                    </text>
                  ) : null}
                </g>
              ))}
            </g>
          ))}
          {through && minutesFrom(first, through) > 0 && minutesFrom(first, through) < total ? (
            <line className="mf-tl__now" x1={x(through)} x2={x(through)} y1={PLOT_TOP - 4} y2={PLOT_TOP + plotH} />
          ) : null}
          {path(points, "")}
          {marks}
          {blockedMarks}
        </g>
      </svg>
    </div>
  );
}
